// Base map style from StadiaMaps (Outdoors theme).
// Route data (popularLegs, routeLayers) lives in ./routeData.ts to avoid
// bundling the 204KB top-booked-legs JSON into pages that only need the style.

// BASE STYLE //

export const baseMapStyle = {
  center: [8.542, 47.372],
  glyphs: 'https://tiles.stadiamaps.com/fonts/{fontstack}/{range}.pbf',
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: {
        'background-color': '#f9f3ea'
      }
    },
    {
      id: 'landcover-glacier',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['==', 'subclass', 'glacier'],
      layout: {
        visibility: 'visible'
      },
      paint: {
        'fill-color': '#fff',
        'fill-opacity': {
          base: 1,
          stops: [
            [0, 0.9],
            [10, 0.3]
          ]
        }
      }
    },
    {
      id: 'park',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'park',
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': '#d8e8c8',
        'fill-opacity': {
          base: 1.8,
          stops: [
            [6, 0.5],
            [12, 0.2]
          ]
        }
      }
    },
    {
      id: 'landuse-cemetery',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: ['==', 'class', 'cemetery'],
      paint: {
        'fill-color': '#e0e4dd'
      }
    },
    {
      id: 'landuse-railway',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landuse',
      filter: ['==', 'class', 'railway'],
      paint: {
        'fill-color': 'hsla(30, 19%, 90%, 0.4)'
      }
    },
    {
      id: 'landcover-grass-park',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'park',
      filter: ['in', 'class', 'public_park', 'national_park', 'nature_reserve'],
      paint: {
        'fill-color': '#d5e8c2',
        'fill-outline-color': 'hsla(0, 0%, 0%, 0.1)',
        'fill-antialias': {
          base: 1,
          stops: [
            [0, false],
            [9, true]
          ]
        }
      }
    },
    {
      id: 'landcover-rock',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['all', ['==', 'class', 'rock']],
      paint: {
        'fill-color': 'hsla(0, 0%, 85%, 1)',
        'fill-outline-color': 'hsla(0, 0%, 0%, 0.1)',
        'fill-opacity': 1
      }
    },
    {
      id: 'landcover-sand',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['all', ['==', 'class', 'sand']],
      paint: {
        'fill-color': 'hsla(44, 70%, 87%, 1)',
        'fill-outline-color': 'hsla(0, 0%, 0%, 0.1)',
        'fill-opacity': 1
      }
    },
    {
      id: 'landcover-wetland',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['==', 'class', 'wetland'],
      paint: {
        'fill-color': '#3D85C6',
        'fill-opacity': 0.075,
        'fill-outline-color': 'hsla(0, 0%, 0%, 0.03)',
        'fill-antialias': {
          base: 1,
          stops: [
            [0, false],
            [9, true]
          ]
        }
      }
    },
    {
      id: 'landcover-wood',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['==', 'class', 'wood'],
      paint: {
        'fill-color': '#90d86c',
        'fill-opacity': 0.15,
        'fill-outline-color': 'hsla(0, 0%, 0%, 0.2)',
        'fill-antialias': {
          base: 1,
          stops: [
            [0, false],
            [9, true]
          ]
        }
      }
    },
    {
      id: 'landcover-grass',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['==', 'class', 'grass'],
      paint: {
        'fill-color': '#d8e8c8',
        'fill-opacity': 1
      }
    },
    {
      id: 'waterway-other',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'waterway',
      filter: ['!in', 'class', 'canal', 'river', 'stream'],
      layout: {
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#a0c8f0',
        'line-width': {
          base: 1.3,
          stops: [
            [13, 0.5],
            [20, 2]
          ]
        }
      }
    },
    {
      id: 'waterway-stream-canal',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'waterway',
      filter: ['in', 'class', 'canal', 'stream'],
      layout: {
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#a0c8f0',
        'line-width': {
          base: 1.3,
          stops: [
            [13, 0.5],
            [20, 6]
          ]
        }
      }
    },
    {
      id: 'waterway-river',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'waterway',
      filter: ['==', 'class', 'river'],
      layout: {
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#a0c8f0',
        'line-width': {
          base: 1.2,
          stops: [
            [10, 0.8],
            [20, 6]
          ]
        }
      }
    },
    {
      id: 'boundary-land-level-4',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['all', ['in', 'admin_level', 4], ['!=', 'maritime', 1]],
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': '#9e9cab',
        'line-dasharray': [4, 2, 2, 2],
        'line-width': {
          base: 1.4,
          stops: [
            [4, 0.4],
            [5, 0.8],
            [12, 2.5]
          ]
        },
        'line-opacity': 0.5
      }
    },
    {
      id: 'boundary-land-level-2',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['all', ['==', 'admin_level', 2], ['!=', 'maritime', 1], ['!=', 'disputed', 1]],
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': 'hsl(248, 7%, 66%)',
        'line-width': {
          base: 1,
          stops: [
            [0, 0.6],
            [4, 1.4],
            [5, 2],
            [12, 4]
          ]
        }
      }
    },
    {
      id: 'boundary-land-disputed',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'boundary',
      filter: ['all', ['!=', 'maritime', 1], ['==', 'disputed', 1]],
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': 'hsl(248, 7%, 70%)',
        'line-dasharray': [1, 3],
        'line-width': {
          base: 1,
          stops: [
            [0, 0.6],
            [4, 1.4],
            [5, 2],
            [12, 4]
          ]
        }
      }
    },
    {
      id: 'water-offset',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'water',
      minzoom: 6,
      maxzoom: 8,
      filter: ['==', '$type', 'Polygon'],
      layout: {
        visibility: 'visible'
      },
      paint: {
        'fill-opacity': 1,
        'fill-color': '#a0c8f0',
        'fill-translate': {
          base: 1,
          stops: [
            [6, [2, 0]],
            [8, [0, 0]]
          ]
        }
      }
    },
    {
      id: 'water',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'water',
      layout: {
        visibility: 'visible'
      },
      paint: {
        'fill-color': 'hsl(210, 60%, 80%)'
      }
    },
    {
      id: 'park-outline',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'park',
      minzoom: 6,
      filter: ['==', '$type', 'Polygon'],
      layout: {},
      paint: {
        'line-color': {
          base: 1,
          stops: [
            [6, 'hsla(96, 40%, 49%, 0.36)'],
            [8, 'hsla(96, 40%, 49%, 0.66)']
          ]
        },
        'line-dasharray': [3, 3]
      }
    },
    {
      id: 'landcover-ice-shelf',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'landcover',
      filter: ['==', 'subclass', 'ice_shelf'],
      layout: {
        visibility: 'visible'
      },
      paint: {
        'fill-color': '#fff',
        'fill-opacity': {
          base: 1,
          stops: [
            [0, 0.9],
            [10, 0.3]
          ]
        }
      }
    },
    {
      id: 'building',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'building',
      paint: {
        'fill-color': {
          base: 1,
          stops: [
            [15.5, '#f2eae2'],
            [16, '#dfdbd7']
          ]
        },
        'fill-antialias': true
      }
    },
    {
      id: 'building-top',
      type: 'fill',
      source: 'openmaptiles',
      'source-layer': 'building',
      layout: {
        visibility: 'visible'
      },
      paint: {
        'fill-translate': {
          base: 1,
          stops: [
            [14, [0, 0]],
            [16, [-2, -2]]
          ]
        },
        'fill-outline-color': '#dfdbd7',
        'fill-color': '#f2eae2',
        'fill-opacity': {
          base: 1,
          stops: [
            [13, 0],
            [16, 1]
          ]
        }
      }
    },
    // {
    //   id: 'tunnel-service-track-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'tunnel'],
    //     ['in', 'class', 'service', 'track'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#cfcdca',
    //     'line-dasharray': [0.5, 0.25],
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [15, 1],
    //         [16, 4],
    //         [20, 11],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-minor-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['all', ['==', 'brunnel', 'tunnel'], ['==', 'class', 'minor']],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#cfcdca',
    //     'line-opacity': {
    //       stops: [
    //         [12, 0],
    //         [12.5, 1],
    //       ],
    //     },
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12, 0.5],
    //         [13, 1],
    //         [14, 4],
    //         [20, 15],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-secondary-tertiary-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'tunnel'],
    //     ['in', 'class', 'secondary', 'tertiary'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-opacity': 1,
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [8, 1.5],
    //         [20, 17],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-trunk-primary-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'tunnel'],
    //     ['in', 'class', 'primary', 'trunk'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [5, 0.4],
    //         [6, 0.6],
    //         [7, 1.5],
    //         [20, 22],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-motorway-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['all', ['==', 'brunnel', 'tunnel'], ['==', 'class', 'motorway']],
    //   layout: {
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-dasharray': [0.5, 0.25],
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [5, 0.4],
    //         [6, 0.6],
    //         [7, 1.5],
    //         [20, 22],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-path',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     [
    //       'all',
    //       ['==', 'brunnel', 'tunnel'],
    //       ['in', 'class', 'path', 'footway'],
    //     ],
    //   ],
    //   paint: {
    //     'line-color': '#cba',
    //     'line-dasharray': [1.5, 0.75],
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [15, 1.2],
    //         [20, 4],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-service-track',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'tunnel'],
    //     ['in', 'class', 'service', 'track'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#fff',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [15.5, 0],
    //         [16, 2],
    //         [20, 7.5],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-minor',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'tunnel'],
    //     ['==', 'class', 'minor_road'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#fff',
    //     'line-opacity': 1,
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [13.5, 0],
    //         [14, 2.5],
    //         [20, 11.5],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-secondary-tertiary',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'tunnel'],
    //     ['in', 'class', 'secondary', 'tertiary'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#fff4c6',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6.5, 0],
    //         [7, 0.5],
    //         [20, 10],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-trunk-primary',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'tunnel'],
    //     ['in', 'class', 'primary', 'trunk'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#fff4c6',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6.5, 0],
    //         [7, 0.5],
    //         [20, 18],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-motorway',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['all', ['==', 'brunnel', 'tunnel'], ['==', 'class', 'motorway']],
    //   layout: {
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#ffdaa6',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6.5, 0],
    //         [7, 0.5],
    //         [20, 18],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'tunnel-railway',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['all', ['==', 'brunnel', 'tunnel'], ['==', 'class', 'rail']],
    //   paint: {
    //     'line-color': '#bbb',
    //     'line-width': {
    //       base: 1.4,
    //       stops: [
    //         [14, 0.4],
    //         [15, 0.75],
    //         [20, 2],
    //       ],
    //     },
    //     'line-dasharray': [2, 2],
    //   },
    // },
    // {
    //   id: 'ferry',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['all', ['in', 'class', 'ferry']],
    //   layout: {
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': 'rgba(108, 159, 182, 1)',
    //     'line-width': 1.1,
    //     'line-dasharray': [2, 2],
    //   },
    // },
    // {
    //   id: 'aeroway-taxiway-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'aeroway',
    //   minzoom: 12,
    //   filter: ['all', ['in', 'class', 'taxiway']],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': 'rgba(153, 153, 153, 1)',
    //     'line-width': {
    //       base: 1.5,
    //       stops: [
    //         [11, 2],
    //         [17, 12],
    //       ],
    //     },
    //     'line-opacity': 1,
    //   },
    // },
    // {
    //   id: 'aeroway-runway-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'aeroway',
    //   minzoom: 12,
    //   filter: ['all', ['in', 'class', 'runway']],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': 'rgba(153, 153, 153, 1)',
    //     'line-width': {
    //       base: 1.5,
    //       stops: [
    //         [11, 5],
    //         [17, 55],
    //       ],
    //     },
    //     'line-opacity': 1,
    //   },
    // },
    // {
    //   id: 'aeroway-area',
    //   type: 'fill',
    //   source: 'openmaptiles',
    //   'source-layer': 'aeroway',
    //   minzoom: 4,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'Polygon'],
    //     ['in', 'class', 'runway', 'taxiway'],
    //   ],
    //   layout: {
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'fill-opacity': {
    //       base: 1,
    //       stops: [
    //         [13, 0],
    //         [14, 1],
    //       ],
    //     },
    //     'fill-color': 'rgba(255, 255, 255, 1)',
    //   },
    // },
    // {
    //   id: 'aeroway-taxiway',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'aeroway',
    //   minzoom: 4,
    //   filter: [
    //     'all',
    //     ['in', 'class', 'taxiway'],
    //     ['==', '$type', 'LineString'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': 'rgba(255, 255, 255, 1)',
    //     'line-width': {
    //       base: 1.5,
    //       stops: [
    //         [11, 1],
    //         [17, 10],
    //       ],
    //     },
    //     'line-opacity': {
    //       base: 1,
    //       stops: [
    //         [11, 0],
    //         [12, 1],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'aeroway-runway',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'aeroway',
    //   minzoom: 4,
    //   filter: ['all', ['in', 'class', 'runway'], ['==', '$type', 'LineString']],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': 'rgba(255, 255, 255, 1)',
    //     'line-width': {
    //       base: 1.5,
    //       stops: [
    //         [11, 4],
    //         [17, 50],
    //       ],
    //     },
    //     'line-opacity': {
    //       base: 1,
    //       stops: [
    //         [11, 0],
    //         [12, 1],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-area',
    //   type: 'fill',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['==', '$type', 'Polygon'],
    //   layout: {
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'fill-color': 'hsla(0, 0%, 89%, 0.56)',
    //     'fill-outline-color': '#cfcdca',
    //     'fill-opacity': 0.9,
    //     'fill-antialias': false,
    //   },
    // },
    // {
    //   id: 'highway-motorway-link-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 12,
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     ['==', 'class', 'motorway_link'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-opacity': 1,
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12, 1],
    //         [13, 3],
    //         [14, 4],
    //         [20, 15],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-link-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 13,
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     [
    //       'in',
    //       'class',
    //       'primary_link',
    //       'secondary_link',
    //       'tertiary_link',
    //       'trunk_link',
    //     ],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-opacity': 1,
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12, 1],
    //         [13, 3],
    //         [14, 4],
    //         [20, 15],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-minor-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     [
    //       'all',
    //       ['!=', 'brunnel', 'tunnel'],
    //       ['in', 'class', 'minor', 'service', 'track'],
    //     ],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#cfcdca',
    //     'line-opacity': {
    //       stops: [
    //         [12, 0],
    //         [12.5, 1],
    //       ],
    //     },
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12, 0.5],
    //         [13, 1],
    //         [14, 4],
    //         [20, 15],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-secondary-tertiary-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     ['in', 'class', 'secondary', 'tertiary'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-opacity': 1,
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6, 1.5],
    //         [20, 17],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-primary-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 5,
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     ['in', 'class', 'primary'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [5, 0.5],
    //         [8, 0.6],
    //         [9, 1.5],
    //         [20, 22],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-trunk-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 5,
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     ['in', 'class', 'trunk'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-opacity': {
    //       stops: [
    //         [5, 0],
    //         [6, 1],
    //       ],
    //     },
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [5, 0],
    //         [6, 0.6],
    //         [7, 1.5],
    //         [20, 22],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-motorway-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 4,
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     ['==', 'class', 'motorway'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [4, 0],
    //         [5, 0.4],
    //         [6, 0.6],
    //         [7, 1.5],
    //         [20, 22],
    //       ],
    //     },
    //     'line-opacity': {
    //       stops: [
    //         [4, 0],
    //         [5, 1],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-path',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     [
    //       'all',
    //       ['!in', 'brunnel', 'bridge', 'tunnel'],
    //       ['in', 'class', 'path', 'footway'],
    //     ],
    //   ],
    //   paint: {
    //     'line-color': '#cba',
    //     'line-dasharray': {
    //       stops: [
    //         [13, [1, 0.5]],
    //         [14, [2, 1]],
    //         [20, [3, 1]],
    //       ],
    //     },
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [13, 1],
    //         [14, 2],
    //         [20, 4],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-motorway-link',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 12,
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     ['==', 'class', 'motorway_link'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e5b87e',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12.5, 0],
    //         [13, 1.5],
    //         [14, 2.5],
    //         [20, 11.5],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-link',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 13,
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     [
    //       'in',
    //       'class',
    //       'primary_link',
    //       'secondary_link',
    //       'tertiary_link',
    //       'trunk_link',
    //     ],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e5d7a0',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12.5, 0],
    //         [13, 1.5],
    //         [14, 2.5],
    //         [20, 11.5],
    //       ],
    //     },
    //   },
    // },
    {
      id: 'ski-slopes-line',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'piste',
      filter: ['all', ['in', 'class', 'downhill', 'nordic'], ['==', '$type', 'LineString']],
      paint: {
        'line-color': 'rgba(75, 122, 195, 1)',
        'line-width': {
          stops: [
            [10, 1],
            [13, 1.5]
          ]
        },
        'line-blur': 0.5,
        'line-dasharray': [3, 2]
      },
      minzoom: 11
    },
    // {
    //   id: 'highway-minor',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     [
    //       'all',
    //       ['!=', 'brunnel', 'tunnel'],
    //       ['in', 'class', 'minor', 'service', 'track'],
    //     ],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#fff',
    //     'line-opacity': 1,
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [13.5, 0],
    //         [14, 2.5],
    //         [20, 11.5],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-secondary-tertiary',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['!in', 'brunnel', 'bridge', 'tunnel'],
    //     ['in', 'class', 'secondary', 'tertiary'],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e5d7a0',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6.5, 0],
    //         [8, 0.5],
    //         [20, 13],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-primary',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     [
    //       'all',
    //       ['!in', 'brunnel', 'bridge', 'tunnel'],
    //       ['in', 'class', 'primary'],
    //     ],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e5d7a0',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [5, 0.1],
    //         [9, 0.5],
    //         [20, 18],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-trunk',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     [
    //       'all',
    //       ['!in', 'brunnel', 'bridge', 'tunnel'],
    //       ['in', 'class', 'trunk'],
    //     ],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e5d7a0',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6.5, 0],
    //         [7, 0.5],
    //         [20, 18],
    //       ],
    //     },
    //   },
    // },
    // {
    //   id: 'highway-motorway',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   minzoom: 5,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     [
    //       'all',
    //       ['!in', 'brunnel', 'bridge', 'tunnel'],
    //       ['==', 'class', 'motorway'],
    //     ],
    //   ],
    //   layout: {
    //     'line-cap': 'round',
    //     'line-join': 'round',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'line-color': '#e5b87e',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6.5, 0],
    //         [7, 0.5],
    //         [20, 10],
    //       ],
    //     },
    //   },
    // },
    {
      id: 'railway-transit',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', '$type', 'LineString'], ['all', ['==', 'class', 'transit'], ['!in', 'brunnel', 'tunnel']]],
      layout: {
        visibility: 'visible'
      },
      paint: {
        'line-color': 'hsla(0, 0%, 73%, 0.77)',
        'line-width': {
          base: 1.4,
          stops: [
            [14, 0.4],
            [20, 1]
          ]
        }
      }
    },
    {
      id: 'railway-transit-hatching',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', '$type', 'LineString'], ['all', ['==', 'class', 'transit'], ['!in', 'brunnel', 'tunnel']]],
      layout: {
        visibility: 'visible'
      },
      paint: {
        'line-color': 'hsla(0, 0%, 73%, 0.68)',
        'line-dasharray': [0.2, 8],
        'line-width': {
          base: 1.4,
          stops: [
            [14.5, 0],
            [15, 2],
            [20, 6]
          ]
        }
      }
    },
    {
      id: 'railway-service',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', '$type', 'LineString'], ['all', ['==', 'class', 'rail'], ['has', 'service']]],
      paint: {
        'line-color': 'hsla(0, 0%, 73%, 0.77)',
        'line-width': {
          base: 1.4,
          stops: [
            [14, 0.4],
            [20, 1]
          ]
        }
      }
    },
    {
      id: 'railway-service-hatching',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', '$type', 'LineString'], ['all', ['==', 'class', 'rail'], ['has', 'service']]],
      layout: {
        visibility: 'visible'
      },
      paint: {
        'line-color': 'hsla(0, 0%, 73%, 0.68)',
        'line-dasharray': [0.2, 8],
        'line-width': {
          base: 1.4,
          stops: [
            [14.5, 0],
            [15, 2],
            [20, 6]
          ]
        }
      }
    },
    {
      id: 'railway',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: [
        'all',
        ['==', '$type', 'LineString'],
        ['all', ['!has', 'service'], ['!in', 'brunnel', 'bridge', 'tunnel'], ['==', 'class', 'rail']]
      ],
      paint: {
        'line-color': '#bbb',
        'line-width': {
          base: 1.4,
          stops: [
            [14, 0.4],
            [15, 0.75],
            [20, 2]
          ]
        }
      }
    },
    {
      id: 'railway-hatching',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: [
        'all',
        ['==', '$type', 'LineString'],
        ['all', ['!has', 'service'], ['!in', 'brunnel', 'bridge', 'tunnel'], ['==', 'class', 'rail']]
      ],
      paint: {
        'line-color': '#bbb',
        'line-dasharray': [0.2, 8],
        'line-width': {
          base: 1.4,
          stops: [
            [14.5, 0],
            [15, 3],
            [20, 8]
          ]
        }
      }
    },
    // {
    //   id: 'bridge-motorway-link-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'bridge'],
    //     ['==', 'class', 'motorway_link'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-opacity': 1,
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12, 1],
    //         [13, 3],
    //         [14, 4],
    //         [20, 15],
    //       ],
    //     },
    //   },
    // },
    {
      id: 'bridge-link-casing',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: [
        'all',
        ['==', 'brunnel', 'bridge'],
        ['in', 'class', 'primary_link', 'secondary_link', 'tertiary_link', 'trunk_link']
      ],
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': '#e9ac77',
        'line-opacity': 1,
        'line-width': {
          base: 1.2,
          stops: [
            [12, 1],
            [13, 3],
            [14, 4],
            [20, 15]
          ]
        }
      }
    },
    {
      id: 'bridge-secondary-tertiary-casing',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', 'brunnel', 'bridge'], ['in', 'class', 'secondary', 'tertiary']],
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': '#e9ac77',
        'line-opacity': 1,
        'line-width': {
          base: 1.2,
          stops: [
            [8, 1.5],
            [20, 28]
          ]
        }
      }
    },
    {
      id: 'bridge-trunk-primary-casing',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', 'brunnel', 'bridge'], ['in', 'class', 'primary', 'trunk']],
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': 'hsl(28, 76%, 67%)',
        'line-width': {
          base: 1.2,
          stops: [
            [5, 0.4],
            [6, 0.6],
            [7, 1.5],
            [20, 26]
          ]
        }
      }
    },
    // {
    //   id: 'bridge-motorway-casing',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['all', ['==', 'brunnel', 'bridge'], ['==', 'class', 'motorway']],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e9ac77',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [5, 0.4],
    //         [6, 0.6],
    //         [7, 1.5],
    //         [20, 22],
    //       ],
    //     },
    //   },
    // },
    {
      id: 'bridge-path-casing',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: [
        'all',
        ['==', '$type', 'LineString'],
        ['all', ['==', 'brunnel', 'bridge'], ['in', 'class', 'path', 'footway']]
      ],
      paint: {
        'line-color': '#f8f4f0',
        'line-width': {
          base: 1.2,
          stops: [
            [15, 1.2],
            [20, 18]
          ]
        }
      }
    },
    {
      id: 'bridge-path',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: [
        'all',
        ['==', '$type', 'LineString'],
        ['all', ['==', 'brunnel', 'bridge'], ['in', 'class', 'path', 'footway']]
      ],
      paint: {
        'line-color': '#cba',
        'line-width': {
          base: 1.2,
          stops: [
            [15, 1.2],
            [20, 4]
          ]
        },
        'line-dasharray': [1.5, 0.75]
      }
    },
    // {
    //   id: 'bridge-motorway-link',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: [
    //     'all',
    //     ['==', 'brunnel', 'bridge'],
    //     ['==', 'class', 'motorway_link'],
    //   ],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e5b87e',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [12.5, 0],
    //         [13, 1.5],
    //         [14, 2.5],
    //         [20, 11.5],
    //       ],
    //     },
    //   },
    // },
    {
      id: 'bridge-link',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: [
        'all',
        ['==', 'brunnel', 'bridge'],
        ['in', 'class', 'primary_link', 'secondary_link', 'tertiary_link', 'trunk_link']
      ],
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': '#e5d7a0',
        'line-width': {
          base: 1.2,
          stops: [
            [12.5, 0],
            [13, 1.5],
            [14, 2.5],
            [20, 11.5]
          ]
        }
      }
    },
    {
      id: 'bridge-secondary-tertiary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', 'brunnel', 'bridge'], ['in', 'class', 'secondary', 'tertiary']],
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': '#e5d7a0',
        'line-width': {
          base: 1.2,
          stops: [
            [6.5, 0],
            [7, 0.5],
            [20, 20]
          ]
        }
      }
    },
    {
      id: 'bridge-trunk-primary',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', 'brunnel', 'bridge'], ['in', 'class', 'primary', 'trunk']],
      layout: {
        'line-join': 'round'
      },
      paint: {
        'line-color': '#e5d7a0',
        'line-width': {
          base: 1.2,
          stops: [
            [6.5, 0],
            [7, 0.5],
            [20, 18]
          ]
        }
      }
    },
    // {
    //   id: 'bridge-motorway',
    //   type: 'line',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation',
    //   filter: ['all', ['==', 'brunnel', 'bridge'], ['==', 'class', 'motorway']],
    //   layout: {
    //     'line-join': 'round',
    //   },
    //   paint: {
    //     'line-color': '#e5b87e',
    //     'line-width': {
    //       base: 1.2,
    //       stops: [
    //         [6.5, 0],
    //         [7, 0.5],
    //         [20, 18],
    //       ],
    //     },
    //   },
    // },
    {
      id: 'bridge-railway',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', 'brunnel', 'bridge'], ['==', 'class', 'rail']],
      paint: {
        'line-color': '#bbb',
        'line-width': {
          base: 1.4,
          stops: [
            [14, 0.4],
            [15, 0.75],
            [20, 2]
          ]
        }
      }
    },
    {
      id: 'bridge-railway-hatching',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      filter: ['all', ['==', 'brunnel', 'bridge'], ['==', 'class', 'rail']],
      paint: {
        'line-color': '#bbb',
        'line-dasharray': [0.2, 8],
        'line-width': {
          base: 1.4,
          stops: [
            [14.5, 0],
            [15, 3],
            [20, 8]
          ]
        }
      }
    },
    {
      id: 'cablecar',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      minzoom: 11,
      filter: ['==', 'class', 'cable_car'],
      layout: {
        visibility: 'visible',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'hsl(0, 0%, 70%)',
        'line-width': {
          base: 1,
          stops: [
            [11, 1],
            [19, 2.5]
          ]
        }
      }
    },
    {
      id: 'cablecar-dash',
      type: 'line',
      source: 'openmaptiles',
      'source-layer': 'transportation',
      minzoom: 11,
      filter: ['==', 'class', 'cable_car'],
      layout: {
        visibility: 'visible',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'hsl(0, 0%, 70%)',
        'line-width': {
          base: 1,
          stops: [
            [11, 3],
            [19, 5.5]
          ]
        },
        'line-dasharray': [2, 3]
      }
    },
    // {
    //   id: 'waterway-name',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'waterway',
    //   minzoom: 13,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     ['has', 'name'],
    //     ['!=', 'name', ''],
    //   ],
    //   layout: {
    //     'text-font': ['Stadia Italic'],
    //     'text-size': 14,
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-max-width': 5,
    //     'text-rotation-alignment': 'map',
    //     'text-pitch-alignment': 'viewport',
    //     'symbol-placement': 'line',
    //     'text-letter-spacing': 0.2,
    //     'symbol-spacing': 350,
    //   },
    //   paint: {
    //     'text-color': '#115AA7',
    //   },
    // },
    // {
    //   id: 'water-name-other',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'water_name',
    //   minzoom: 1,
    //   filter: ['all', ['==', '$type', 'Point'], ['!in', 'class', 'ocean']],
    //   layout: {
    //     'text-font': ['Stadia Italic'],
    //     'text-size': {
    //       stops: [
    //         [0, 10],
    //         [6, 14],
    //       ],
    //     },
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-max-width': 5,
    //     'text-line-height': 1.55,
    //     'symbol-placement': 'point',
    //     'symbol-spacing': 150,
    //     'text-letter-spacing': 0.2,
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'text-color': '#115AA7',
    //   },
    // },
    // {
    //   id: 'water-name-ocean',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'water_name',
    //   filter: ['all', ['==', '$type', 'Point'], ['==', 'class', 'ocean']],
    //   layout: {
    //     'text-font': ['Stadia Italic'],
    //     'text-size': 14,
    //     'text-field': '{name:en}',
    //     'text-max-width': 5,
    //     'symbol-placement': 'point',
    //     'symbol-spacing': 350,
    //     'symbol-avoid-edges': true,
    //     'text-letter-spacing': 0.2,
    //   },
    //   paint: {
    //     'text-color': '#115AA7',
    //   },
    // },
    // {
    //   id: 'poi-level-3',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'poi',
    //   minzoom: 16,
    //   filter: ['all', ['==', '$type', 'Point'], ['\u003E=', 'rank', 25]],
    //   layout: {
    //     visibility: 'none',
    //     'text-padding': 2,
    //     'text-font': ['Stadia Semibold'],
    //     'text-anchor': 'top',
    //     'icon-image': '{class}_11',
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-offset': [0, 0.6],
    //     'text-size': 12,
    //     'text-max-width': 9,
    //     'text-line-height': 1.55,
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-halo-blur': 0.5,
    //     'text-color': '#666',
    //     'text-halo-width': 1,
    //     'text-halo-color': '#ffffff',
    //   },
    // },
    // {
    //   id: 'poi-level-2',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'poi',
    //   minzoom: 15,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'Point'],
    //     ['\u003C=', 'rank', 24],
    //     ['\u003E=', 'rank', 15],
    //   ],
    //   layout: {
    //     visibility: 'none',
    //     'text-padding': 2,
    //     'text-font': ['Stadia Semibold'],
    //     'text-anchor': 'top',
    //     'icon-image': '{class}_11',
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-offset': [0, 0.6],
    //     'text-size': 12,
    //     'text-max-width': 9,
    //     'text-line-height': 1.55,
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-halo-blur': 0.5,
    //     'text-color': '#666',
    //     'text-halo-width': 1,
    //     'text-halo-color': '#ffffff',
    //   },
    // },
    // {
    //   id: 'poi-level-1',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'poi',
    //   minzoom: 14,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'Point'],
    //     ['<=', 'rank', 14],
    //     ['has', 'name'],
    //     ['!=', 'name', ''],
    //   ],
    //   layout: {
    //     visibility: 'none',
    //     'text-padding': 2,
    //     'text-font': ['Stadia Semibold'],
    //     'text-anchor': 'top',
    //     'icon-image': '{class}_11',
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-offset': [0, 0.6],
    //     'text-size': 12,
    //     'text-max-width': 9,
    //     'text-line-height': 1.55,
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-halo-blur': 0.5,
    //     'text-color': '#666',
    //     'text-halo-width': 1,
    //     'text-halo-color': '#ffffff',
    //   },
    // },
    {
      id: 'poi-railway',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'poi',
      minzoom: 13,
      filter: [
        'all',
        ['==', '$type', 'Point'],
        ['has', 'name'],
        ['!=', 'name', ''],
        ['==', 'class', 'railway'],
        ['==', 'subclass', 'station']
      ],
      layout: {
        visibility: 'none',
        'text-padding': 2,
        'text-font': ['Stadia Regular'],
        'text-anchor': 'top',
        'icon-image': '{class}_11',
        'text-field': ['concat', ['get', 'name:en']],
        'text-offset': [0, 0.6],
        'text-size': 12,
        'text-max-width': 9,
        'text-line-height': 1.55,
        'icon-optional': false,
        'icon-ignore-placement': false,
        'icon-allow-overlap': false,
        'text-ignore-placement': false,
        'text-allow-overlap': false,
        'text-optional': true,
        'symbol-avoid-edges': true
      },
      paint: {
        'text-halo-blur': 0.5,
        'text-color': '#666',
        'text-halo-width': 1,
        'text-halo-color': '#ffffff'
      }
    },
    // {
    //   id: 'highway-name-path',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation_name',
    //   minzoom: 15.5,
    //   filter: ['in', 'class', 'path', 'footway'],
    //   layout: {
    //     'text-size': {
    //       base: 1,
    //       stops: [
    //         [13, 12],
    //         [14, 13],
    //       ],
    //     },
    //     'text-font': ['Stadia Regular'],
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'symbol-placement': 'line',
    //     'text-rotation-alignment': 'map',
    //     'text-pitch-alignment': 'viewport',
    //   },
    //   paint: {
    //     'text-halo-color': '#f8f4f0',
    //     'text-color': 'hsl(30, 23%, 62%)',
    //     'text-halo-width': 0.5,
    //   },
    // },
    // {
    //   id: 'ski-slopes-label',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'piste',
    //   layout: {
    //     'text-field': '{name}',
    //     'text-font': ['Stadia Regular'],
    //     'text-size': 12,
    //     'symbol-placement': 'line-center',
    //     'icon-pitch-alignment': 'auto',
    //     'text-anchor': 'bottom',
    //     'text-keep-upright': true,
    //   },
    //   minzoom: 13,
    //   paint: {
    //     'text-color': 'rgba(75, 122, 195, 1)',
    //     'text-halo-color': 'rgba(255, 255, 255, 1)',
    //     'text-halo-blur': 0.5,
    //     'text-halo-width': 1,
    //   },
    // },
    // {
    //   id: 'highway-name-minor',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation_name',
    //   minzoom: 15,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'LineString'],
    //     ['in', 'class', 'minor', 'service', 'track'],
    //   ],
    //   layout: {
    //     'text-size': {
    //       base: 1,
    //       stops: [
    //         [13, 12],
    //         [14, 13],
    //       ],
    //     },
    //     'text-font': ['Stadia Regular'],
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'symbol-placement': 'line',
    //     'text-rotation-alignment': 'map',
    //     'text-pitch-alignment': 'viewport',
    //   },
    //   paint: {
    //     'text-halo-blur': 0.5,
    //     'text-color': '#765',
    //     'text-halo-width': 1,
    //   },
    // },
    // {
    //   id: 'highway-name-major',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation_name',
    //   minzoom: 12.2,
    //   filter: ['in', 'class', 'primary', 'secondary', 'tertiary', 'trunk'],
    //   layout: {
    //     'text-size': {
    //       base: 1,
    //       stops: [
    //         [13, 12],
    //         [14, 13],
    //       ],
    //     },
    //     'text-font': ['Stadia Regular'],
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'symbol-placement': 'line',
    //     'text-rotation-alignment': 'map',
    //     'text-pitch-alignment': 'viewport',
    //   },
    //   paint: {
    //     'text-halo-blur': 0.5,
    //     'text-color': '#765',
    //     'text-halo-width': 1,
    //   },
    // },
    // {
    //   id: 'highway-shield-other',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation_name',
    //   minzoom: 8,
    //   filter: [
    //     'all',
    //     ['\u003C=', 'ref_length', 6],
    //     ['==', '$type', 'LineString'],
    //     ['!in', 'network', 'us-interstate', 'us-highway', 'us-state'],
    //   ],
    //   layout: {
    //     'text-size': 10,
    //     'icon-image': 'road_{ref_length}',
    //     'icon-rotation-alignment': 'viewport',
    //     'text-font': ['Stadia Semibold'],
    //     'symbol-placement': {
    //       base: 1,
    //       stops: [
    //         [10, 'point'],
    //         [11, 'line'],
    //       ],
    //     },
    //     'text-rotation-alignment': 'viewport',
    //     'icon-size': 1,
    //     'text-field': '{ref}',
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {},
    // },
    // {
    //   id: 'highway-shield-us-interstate',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation_name',
    //   minzoom: 7,
    //   filter: [
    //     'all',
    //     ['\u003C=', 'ref_length', 6],
    //     ['==', '$type', 'LineString'],
    //     ['in', 'network', 'us-interstate'],
    //   ],
    //   layout: {
    //     'text-size': 10,
    //     'icon-image': '{network}_{ref_length}',
    //     'icon-rotation-alignment': 'viewport',
    //     'text-font': ['Stadia Semibold'],
    //     'symbol-placement': {
    //       base: 1,
    //       stops: [
    //         [7, 'point'],
    //         [7, 'line'],
    //         [8, 'line'],
    //       ],
    //     },
    //     'text-rotation-alignment': 'viewport',
    //     'icon-size': 1,
    //     'text-field': '{ref}',
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-color': 'rgba(0, 0, 0, 1)',
    //   },
    // },
    // {
    //   id: 'highway-shield-us-other',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'transportation_name',
    //   minzoom: 9,
    //   filter: [
    //     'all',
    //     ['\u003C=', 'ref_length', 6],
    //     ['==', '$type', 'LineString'],
    //     ['in', 'network', 'us-highway', 'us-state'],
    //   ],
    //   layout: {
    //     'text-size': 10,
    //     'icon-image': '{network}_{ref_length}',
    //     'icon-rotation-alignment': 'viewport',
    //     'text-font': ['Stadia Semibold'],
    //     'symbol-placement': {
    //       base: 1,
    //       stops: [
    //         [10, 'point'],
    //         [11, 'line'],
    //       ],
    //     },
    //     'text-rotation-alignment': 'viewport',
    //     'icon-size': 1,
    //     'text-field': '{ref}',
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-color': 'rgba(0, 0, 0, 1)',
    //   },
    // },
    // {
    //   id: 'airport-label-major',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'aerodrome_label',
    //   minzoom: 10,
    //   filter: ['all', ['has', 'iata']],
    //   layout: {
    //     'text-padding': 2,
    //     'text-font': ['Stadia Regular'],
    //     'text-anchor': 'top',
    //     'icon-image': 'airport_11',
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-offset': [0, 0.6],
    //     'text-size': 12,
    //     'text-max-width': 9,
    //     'text-line-height': 1.55,
    //     visibility: 'visible',
    //     'icon-size': 1,
    //     'text-optional': true,
    //   },
    //   paint: {
    //     'text-halo-blur': 0.5,
    //     'text-color': '#666',
    //     'text-halo-width': 1,
    //     'text-halo-color': '#ffffff',
    //   },
    // },

    // {
    //   id: 'mountain-peaks',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'mountain_peak',
    //   minzoom: 9,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'Point'],
    //     ['\u003C=', 'rank', 2],
    //     ['has', 'name'],
    //     ['!=', 'name', ''],
    //   ],
    //   layout: {
    //     'text-padding': 2,
    //     'text-font': ['Stadia Regular'],
    //     'text-anchor': 'top',
    //     'icon-image': 'mountain_11',
    //     'text-field': [
    //       'concat',
    //       ['get', 'name:en'],
    //       '\n',
    //       ['to-string', ['get', 'ele']],
    //       ' m',
    //     ],
    //     'text-offset': [0, 0.6],
    //     'text-size': 12,
    //     'text-max-width': 9,
    //     'text-line-height': 1.55,
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-halo-blur': 0.5,
    //     'text-color': '#666',
    //     'text-halo-width': 1,
    //     'text-halo-color': '#ffffff',
    //   },
    // },
    // {
    //   id: 'mountain-peaks-important',
    //   minzoom: 7,
    //   maxzoom: 9,
    //   filter: [
    //     'all',
    //     ['==', '$type', 'Point'],
    //     ['\u003E=', 'ele', 2500],
    //     ['has', 'name'],
    //     ['!=', 'name', ''],
    //     ['\u003C=', 'rank', 1],
    //   ],
    //   layout: {
    //     'icon-image': 'mountain_11',
    //     'symbol-avoid-edges': true,
    //     'text-anchor': 'top',
    //     'text-field': [
    //       'concat',
    //       ['get', 'name:en'],
    //       '\n',
    //       ['to-string', ['get', 'ele']],
    //       ' m',
    //     ],
    //     'text-font': ['Stadia Semibold'],
    //     'text-line-height': 1.55,
    //     'text-max-width': 9,
    //     'text-offset': [0, 0.6],
    //     'text-padding': 2,
    //     'text-size': 12,
    //   },
    //   paint: {
    //     'text-color': '#666',
    //     'text-halo-blur': 0.5,
    //     'text-halo-color': '#ffffff',
    //     'text-halo-width': 1,
    //   },
    //   source: 'openmaptiles',
    //   'source-layer': 'mountain_peak',
    //   type: 'symbol',
    // },
    // {
    //   id: 'park-reserve',
    //   filter: [
    //     'all',
    //     [
    //       'match',
    //       ['get', 'class'],
    //       ['nature_reserve', 'protected_area'],
    //       true,
    //       false,
    //     ],
    //     ['==', ['geometry-type'], 'Point'],
    //     ['==', ['get', 'rank'], 1],
    //     [
    //       '!',
    //       [
    //         'any',
    //         ['in', 'Open Access Land', ['get', 'name']],
    //         ['in', 'State Game Land', ['get', 'name']],
    //       ],
    //     ],
    //   ],
    //   layout: {
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-font': ['Stadia Italic'],
    //     'text-line-height': 1.55,
    //     'text-max-width': 10,
    //     'text-size': {
    //       base: 1.2,
    //       stops: [
    //         [8, 12],
    //         [13, 14],
    //       ],
    //     },
    //     visibility: 'visible',
    //     'symbol-placement': 'point',
    //     'symbol-avoid-edges': true,
    //     'text-padding': 2,
    //     'text-anchor': 'center',
    //     'text-justify': 'center',
    //   },
    //   paint: {
    //     'text-color': 'rgba(102, 51, 1, 1)',
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //     'text-halo-width': 1,
    //   },
    //   source: 'openmaptiles',
    //   'source-layer': 'park',
    //   type: 'symbol',
    //   minzoom: 8,
    // },
    // {
    //   id: 'park-national',
    //   filter: [
    //     'all',
    //     ['==', 'class', 'national_park'],
    //     ['==', '$type', 'Point'],
    //     ['==', 'rank', 1],
    //   ],
    //   layout: {
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-font': ['Stadia Italic'],
    //     'text-line-height': 1.55,
    //     'text-max-width': 10,
    //     'text-size': {
    //       base: 1.2,
    //       stops: [
    //         [6, 12],
    //         [8, 14],
    //         [12, 16],
    //       ],
    //     },
    //     visibility: 'visible',
    //     'symbol-placement': 'point',
    //     'symbol-avoid-edges': true,
    //     'text-padding': 2,
    //     'text-anchor': 'center',
    //     'text-justify': 'center',
    //   },
    //   paint: {
    //     'text-color': 'rgba(102, 51, 1, 1)',
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //     'text-halo-width': 1,
    //   },
    //   source: 'openmaptiles',
    //   'source-layer': 'park',
    //   type: 'symbol',
    //   minzoom: 6,
    // },
    {
      id: 'water-name-lakeline',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'water_name',
      filter: ['==', '$type', 'LineString'],
      layout: {
        'text-font': ['Stadia Italic'],
        'text-size': {
          base: 1.2,
          stops: [
            [0, 10],
            [14, 14]
          ]
        },
        'text-field': ['concat', ['get', 'name:en']],
        'text-max-width': 5,
        'text-line-height': 1.55,
        'text-rotation-alignment': 'map',
        'text-pitch-alignment': 'viewport',
        'symbol-placement': 'line',
        'symbol-spacing': 250,
        'text-letter-spacing': 0.2
      },
      paint: {
        'text-color': '#115AA7'
      }
    },

    //////////// Places ////////////

    // {
    //   id: 'place-other',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   minzoom: 12,
    //   filter: [
    //     'all',
    //     ['!in', 'class', 'city', 'town', 'village', 'country', 'continent'],
    //     PLACE_FILTER,
    //   ],
    //   layout: {
    //     'text-letter-spacing': 0.1,
    //     'text-size': {
    //       base: 1.2,
    //       stops: [
    //         [12, 10],
    //         [15, 14],
    //       ],
    //     },
    //     'text-font': ['Stadia Semibold'],
    //     'text-field': [
    //       'coalesce',
    //       ['get', 'name:en'],
    //       ['get', 'name:latin'],
    //       ['get', 'name'],
    //     ],
    //     'text-transform': 'uppercase',
    //     'text-max-width': 9,
    //     'text-line-height': 1.55,
    //     visibility: 'visible',
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-color': '#633',
    //     'text-halo-width': 1.2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },
    // {
    //   id: 'place-village',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   minzoom: 11,
    //   filter: ['all', ['==', 'class', 'village'], PLACE_FILTER],
    //   layout: {
    //     'text-font': ['Stadia Regular'],
    //     'text-size': {
    //       base: 1.2,
    //       stops: [
    //         [10, 12],
    //         [15, 22],
    //       ],
    //     },
    //     'text-field': [
    //       'coalesce',
    //       ['get', 'name:en'],
    //       ['get', 'name:latin'],
    //       ['get', 'name'],
    //       ['get', 'name:local'],
    //     ],
    //     'text-max-width': 8,
    //     'text-line-height': 1.55,
    //     visibility: 'visible',
    //     'symbol-spacing': 350,
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-color': '#333',
    //     'text-halo-width': 1.2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },
    // {
    //   id: 'place-town',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   filter: ['all', ['==', 'class', 'town'], PLACE_FILTER],
    //   layout: {
    //     'text-font': ['Stadia Regular'],
    //     'text-size': {
    //       base: 1.2,
    //       stops: [
    //         [10, 14],
    //         [15, 22],
    //       ],
    //     },
    //     'text-field': [
    //       'coalesce',
    //       ['get', 'name:en'],
    //       ['get', 'name:latin'],
    //       ['get', 'name'],
    //     ],
    //     'text-max-width': 8,
    //     'text-line-height': 1.55,
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'text-color': '#333',
    //     'text-halo-width': 1.2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },
    // {
    //   id: 'place-city',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   filter: [
    //     'all',
    //     ['!=', 'capital', 2],
    //     ['==', 'class', 'city'],
    //     PLACE_FILTER,
    //   ],
    //   layout: {
    //     'text-font': ['Stadia Semibold'],
    //     'text-size': {
    //       base: 1.2,
    //       stops: [
    //         [7, 14],
    //         [11, 24],
    //       ],
    //     },
    //     // Add fallback chain for names
    //     'text-field': [
    //       'coalesce',
    //       ['get', 'name:en'],
    //       ['get', 'name:latin'],
    //       ['get', 'name'],
    //     ],
    //     'text-max-width': 8,
    //     'text-line-height': 1.55,
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'text-color': '#333',
    //     'text-halo-width': 1.2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },
    // {
    //   id: 'place-city-capital',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   filter: [
    //     'all',
    //     ['==', 'capital', 2],
    //     ['==', 'class', 'city'],
    //     PLACE_FILTER,
    //   ],
    //   layout: {
    //     'text-font': ['Stadia Semibold'],
    //     'text-size': {
    //       base: 1.2,
    //       stops: [
    //         [7, 14],
    //         [11, 24],
    //       ],
    //     },
    //     // Add fallback chain for names
    //     'text-field': [
    //       'coalesce',
    //       ['get', 'name:en'],
    //       ['get', 'name:latin'],
    //       ['get', 'name'],
    //       ['get', 'name:local'],
    //     ],
    //     'text-max-width': 8,
    //     'text-line-height': 1.55,
    //     'icon-image': 'star_11',
    //     'text-offset': [0.4, 0],
    //     'icon-size': 0.8,
    //     'text-anchor': 'left',
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'text-color': '#333',
    //     'text-halo-width': 1.2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },

    //////////// Countries ////////////

    // {
    //   id: 'place-country-other',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   minzoom: 1,
    //   filter: [
    //     'all',
    //     ['==', 'class', 'country'],
    //     ['\u003E=', 'rank', 3],
    //     ['!has', 'iso_a2'],
    //   ],
    //   layout: {
    //     'text-font': ['Stadia Italic'],
    //     'text-field': '{name:latin}',
    //     'text-size': {
    //       stops: [
    //         [3, 11],
    //         [7, 17],
    //       ],
    //     },
    //     'text-transform': 'uppercase',
    //     'text-max-width': 6.25,
    //     visibility: 'visible',
    //     'symbol-avoid-edges': true,
    //   },
    //   paint: {
    //     'text-halo-blur': 1,
    //     'text-color': '#334',
    //     'text-halo-width': 2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },
    // {
    //   id: 'place-country-3',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   minzoom: 1,
    //   filter: [
    //     'all',
    //     ['==', 'class', 'country'],
    //     ['\u003E=', 'rank', 3],
    //     ['has', 'iso_a2'],
    //     ['in', 'iso_a2', ...INCLUDED_COUNTRIES],
    //   ],
    //   layout: {
    //     'text-font': ['Stadia Bold'],
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-size': {
    //       stops: [
    //         [3, 11],
    //         [7, 17],
    //       ],
    //     },
    //     'text-max-width': 6.25,
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'text-halo-blur': 1,
    //     'text-color': '#334',
    //     'text-halo-width': 2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },
    // {
    //   id: 'place-country-2',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   minzoom: 1,
    //   filter: [
    //     'all',
    //     ['==', 'class', 'country'],
    //     ['==', 'rank', 2],
    //     ['has', 'iso_a2'],
    //     ['in', 'iso_a2', ...INCLUDED_COUNTRIES],
    //   ],
    //   layout: {
    //     'text-font': ['Stadia Bold'],
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-size': {
    //       stops: [
    //         [2, 11],
    //         [5, 17],
    //       ],
    //     },
    //     'text-max-width': 6.25,
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'text-halo-blur': 1,
    //     'text-color': '#334',
    //     'text-halo-width': 2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },
    // {
    //   id: 'place-country-1',
    //   type: 'symbol',
    //   source: 'openmaptiles',
    //   'source-layer': 'place',
    //   minzoom: 1,
    //   filter: [
    //     'all',
    //     ['==', 'class', 'country'],
    //     ['==', 'rank', 1],
    //     ['has', 'iso_a2'],
    //     ['in', 'iso_a2', ...INCLUDED_COUNTRIES],
    //   ],
    //   layout: {
    //     'text-font': ['Stadia Bold'],
    //     'text-field': ['concat', ['get', 'name:en']],
    //     'text-size': {
    //       stops: [
    //         [1, 11],
    //         [4, 17],
    //       ],
    //     },
    //     'text-max-width': 6.25,
    //     visibility: 'visible',
    //   },
    //   paint: {
    //     'text-halo-blur': 1,
    //     'text-color': '#334',
    //     'text-halo-width': 2,
    //     'text-halo-color': 'rgba(255,255,255,0.8)',
    //   },
    // },

    //////////// Continents ////////////

    {
      id: 'place-continent',
      type: 'symbol',
      source: 'openmaptiles',
      'source-layer': 'place',
      maxzoom: 1,
      filter: ['==', 'class', 'continent'],
      layout: {
        'text-font': ['Stadia Bold'],
        'text-field': '{name:en}',
        'text-size': 14,
        'text-max-width': 6.25,
        'text-transform': 'uppercase',
        visibility: 'visible'
      },
      paint: {
        'text-halo-blur': 1,
        'text-color': '#334',
        'text-halo-width': 2,
        'text-halo-color': 'rgba(255,255,255,0.8)'
      }
    }
  ],
  metadata: {
    'mapbox:autocomposite': false,
    'openmaptiles:version': '3.x'
  },
  name: 'Outdoors',
  pitch: 0,
  sources: {
    openmaptiles: {
      type: 'vector',
      scheme: 'xyz',
      tiles: ['https://tiles.stadiamaps.com/data/openmaptiles/{z}/{x}/{y}.pbf'],
      minzoom: 0,
      maxzoom: 14,
      attribution:
        '<a href="https://stadiamaps.com/" target="_blank">&copy; Stadia Maps</a> <a href="https://openmaptiles.org/" target="_blank" rel="nofollow noopener noreferrer">&copy; OpenMapTiles</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap</a>'
    }
  },
  sprite: 'https://tiles.stadiamaps.com/styles/outdoors/sprite',
  version: 8,
  zoom: 11.6
};

///////////////////////////////////////////////////////////////////////
/////////////////////// END BASE STYLE ////////////////////////////////
///////////////////////////////////////////////////////////////////////
